import React, { useState, useEffect } from 'react';
import HomePageHeader from './HomePageHeader';
import Footer from './Footer';
import { 
  FileCheck, Shield, BarChart, ArrowRight, ChevronLeft, ChevronRight,
  CheckCircle, Search, RefreshCw, BookOpen, Users, Building,
  Briefcase, UserCheck, AlertCircle, TrendingUp, Clock, Award,
  Scale, Lock, FileSearch, Gavel, ShieldCheck, Brain, CircuitBoard,
  FileSignature, Fingerprint, Eye, Database, Server, Zap
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getSubdomainUrl, getAppPublicPathUrl, getRootDomainUrl } from '../utils/tenant';

function Homepage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  const useCases = [
    {
      icon: <AlertCircle className="h-8 w-8 text-blue-800" />,
      title: "Material Risk Detection",
      description: "Identify P&L line items posing material risk based on financial thresholds and company focus."
    },
    {
      icon: <Search className="h-8 w-8 text-blue-800" />,
      title: "Control Mapping & Documentation",
      description: "Auto-generate flow diagrams and map them to the right controls using templates—no process documentation? No problem."
    },
    {
      icon: <RefreshCw className="h-8 w-8 text-blue-800" />,
      title: "Continuous Control Validation",
      description: "Ditch sample testing. Validate 100% of your data with automated control testing—so you're always audit-ready."
    },
    {
      icon: <BookOpen className="h-8 w-8 text-blue-800" />,
      title: "Audit Issue Tracking",
      description: "Track 100–200+ audit issues across multiple audits with alerts, task assignment, and resolution progress."
    }
  ];

  const personas = [
    {
      icon: <Briefcase className="h-6 w-6 text-blue-800" />,
      title: "CFO / Controller",
      benefits: [
        "Gain visibility into material financial risks before auditors do",
        "Log, track, and certify controls without spreadsheets or silos",
        "Strengthen SOX readiness with less manual effort"
      ]
    },
    {
      icon: <UserCheck className="h-6 w-6 text-blue-800" />,
      title: "Internal Audit / SOX Teams",
      benefits: [
        "Automate testing and risk control mapping in days, not months",
        "Centralize all audit issues and remediation steps in one place",
        "Increase assurance while reducing audit fatigue"
      ]
    },
    {
      icon: <Building className="h-6 w-6 text-blue-800" />,
      title: "External Auditors",
      benefits: [
        "Get structured reports, documented flows, and certification logs",
        "Reduce back-and-forth with validated, auditable evidence",
        "Accelerate quarterly and annual audits with real-time compliance tracking"
      ]
    }
  ];

  const testimonials = [
    {
      quote: "We used to spend weeks documenting controls manually. With this platform, it's done in hours—with flow diagrams generated automatically.",
      author: "Director of Internal Audit",
      company: "Public SaaS Company"
    },
    {
      quote: "Our SOX program finally feels proactive. The system flags risks and gaps before they become audit findings.",
      author: "VP Finance",
      company: "Growth-Stage Fintech"
    },
    {
      quote: "We reduced audit prep time by 40% and closed access violations within hours instead of weeks.",
      author: "IT Compliance Lead",
      company: "Global Retail Brand"
    },
    {
      quote: "This is the closest thing we've seen to UIPath—except it's built for finance and audit.",
      author: "Partner",
      company: "Big 4 Audit Firm"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % useCases.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + useCases.length) % useCases.length);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <HomePageHeader />
      
      <main>
        {/* Hero Section */}
        <div className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-white via-blue-50/20 to-white">
          {/* Professional animated SVG backdrop */}
          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(59, 130, 246, 0.03)" strokeWidth="1"/>
              </pattern>
              <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(59, 130, 246, 0.02)" />
                <stop offset="100%" stopColor="rgba(99, 102, 241, 0.02)" />
              </linearGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
            <circle cx="20%" cy="30%" r="300" fill="url(#grad1)" className="animate-subtle-pulse" />
            <circle cx="80%" cy="70%" r="250" fill="url(#grad1)" className="animate-subtle-pulse animation-delay-2000" />
            <path d="M 0,100 Q 250,50 500,100 T 1000,100 L 1000,0 L 0,0 Z" fill="rgba(59, 130, 246, 0.015)" className="animate-subtle-wave" />
          </svg>
          
          {/* Subtle animated background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(99,102,241,0.05),transparent_50%)]" />
            <div className="absolute top-0 left-0 w-full h-full">
              <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-subtle-float" />
              <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-subtle-float animation-delay-2000" />
              <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-subtle-float animation-delay-4000" />
            </div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              {/* Badge */}
            
              {/* Main heading */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight animate-fadeInUp animation-delay-200">
                Compliance Without{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-800 to-indigo-600">
                  Complexity.
                </span>
              </h1>
              
              {/* Description */}
              <p className="text-xl md:text-2xl text-gray-700 mb-6 max-w-3xl mx-auto leading-relaxed animate-fadeInUp animation-delay-300">
                AI powered process compliance and audit tool for<br />
                Internal Audit, Finance, HR, IT and Legal
              </p>
              
              <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto animate-fadeInUp animation-delay-400">
                All in one intelligent platform | Identify risks | validate controls | stay SOX compliant
              </p>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-6 mb-12 animate-fadeInUp animation-delay-500">
                <div className="flex items-center text-gray-600 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm">
                  <Lock className="h-4 w-4 text-blue-700 mr-2" />
                  <span className="text-sm font-medium">Bank-grade Security</span>
                </div>
                <div className="flex items-center text-gray-600 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm">
                  <Brain className="h-4 w-4 text-blue-700 mr-2" />
                  <span className="text-sm font-medium">AI-Powered Analysis</span>
                </div>
                <div className="flex items-center text-gray-600 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm">
                  <Zap className="h-4 w-4 text-blue-700 mr-2" />
                  <span className="text-sm font-medium">Real-time Monitoring</span>
                </div>
              </div>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp animation-delay-600">
                <a
                  href={getRootDomainUrl('/contact')}
                  className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-blue-800 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 hover:shadow-2xl shadow-lg text-sm sm:text-base"
                >
                  <span className="mr-2">Request a Demo</span>
                  <ArrowRight className="h-4 sm:h-5 w-4 sm:w-5 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href={getAppPublicPathUrl('/signin')}
                  className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-blue-800 border-2 border-blue-200 rounded-lg font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all transform hover:scale-105 hover:shadow-xl shadow-md text-sm sm:text-base"
                >
                  <span className="mr-2">See It in Action</span>
                  <Eye className="h-4 sm:h-5 w-4 sm:w-5 group-hover:scale-110 transition-transform" />
                </a>
              </div>
              
              
            </div>
          </div>
        </div>

        {/* Compliance Stats Bar - Removed, integrated into hero */}

        {/* Use Case Slider */}
        <div className="py-32 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-[-2%]">
            <div className="text-center mb-16 animate-on-scroll" id="use-cases-header">
              <h2 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 transition-all duration-700 transform ${
                isVisible['use-cases-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}>
                Where We Add Value
              </h2>
              <p className={`text-xl text-gray-600 transition-all duration-700 delay-200 transform ${
                isVisible['use-cases-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}>
                Transforming compliance from a burden to a competitive advantage
              </p>
            </div>
            
            <div className="relative">
              <div className="flex items-center justify-center">
                <button
                  onClick={prevSlide}
                  className="absolute left-0 z-10 p-3 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full text-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-110"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                
                <div className="overflow-hidden max-w-3xl mx-auto">
                  <div 
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {useCases.map((useCase, index) => (
                      <div key={index} className="w-full flex-shrink-0 px-4">
                        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-blue-100 relative overflow-hidden group hover:shadow-2xl transition-all duration-300">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full -mr-16 -mt-16 opacity-50 group-hover:scale-110 transition-transform duration-300" />
                          <div className="relative">
                            <div className="flex justify-center mb-6 transform group-hover:scale-110 transition-transform duration-300">
                              <div className="p-3 bg-blue-50 rounded-xl">
                                {React.cloneElement(useCase.icon, { className: "h-8 w-8 text-blue-800" })}
                              </div>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                              {useCase.title}
                            </h3>
                            <p className="text-gray-600 text-center leading-relaxed">
                              {useCase.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={nextSlide}
                  className="absolute right-0 z-10 p-3 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full text-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-110"
                  aria-label="Next slide"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
              
              {/* Slide indicators */}
              <div className="flex justify-center mt-6 space-x-2">
                {useCases.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 rounded-full transition-all ${
                      currentSlide === index ? 'w-8 bg-blue-800' : 'w-2 bg-gray-400'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Persona-Specific Value Statements */}
        <div className="py-32 bg-white relative overflow-hidden">
          {/* Animated gradient orbs */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" />
            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float animation-delay-2000" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 animate-on-scroll" id="personas-header">
              <h2 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 transition-all duration-700 transform ${
                isVisible['personas-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}>
                Built for Every Stakeholder
              </h2>
              <p className={`text-xl text-gray-600 transition-all duration-700 delay-200 transform ${
                isVisible['personas-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}>
                Tailored solutions for your specific compliance challenges
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {personas.map((persona, index) => (
                <div key={index} className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 p-8 border border-blue-100 relative overflow-hidden group animate-on-scroll" id={`persona-${index}`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className={`relative transition-all duration-700 transform delay-${index * 100} ${
                    isVisible[`persona-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}>
                    <div className="flex items-center mb-6">
                      <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-300">
                        {persona.icon}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {persona.title}
                      </h3>
                    </div>
                    <ul className="space-y-3">
                      {persona.benefits.map((benefit, bIndex) => (
                        <li key={bIndex} className="flex items-start group/item">
                          <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0 group-hover/item:scale-110 transition-transform" />
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Customer Quotes */}
        <div className="py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 animate-on-scroll" id="testimonials-header">
              <h2 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 transition-all duration-700 transform ${
                isVisible['testimonials-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}>
                Trusted by Industry Leaders
              </h2>
              <p className={`text-xl text-gray-600 transition-all duration-700 delay-200 transform ${
                isVisible['testimonials-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}>
                See what our customers have to say
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 border border-blue-100 hover:border-blue-300 hover:shadow-xl transition-all duration-300 relative overflow-hidden group animate-on-scroll" id={`testimonial-${index}`}>
                  <div className="absolute top-0 right-0 text-8xl text-blue-100 opacity-10 group-hover:opacity-20 transition-opacity duration-300">"</div>
                  <div className={`relative transition-all duration-700 transform delay-${index * 100} ${
                    isVisible[`testimonial-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}>
                    <div className="mb-4 flex items-center space-x-2">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Award className="h-6 w-6 text-blue-800" />
                      </div>
                      <div className="flex space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="h-4 w-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                          </svg>
                        ))}
                      </div>
                    </div>
                    <blockquote className="text-gray-700 text-lg italic mb-6 leading-relaxed">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="border-t border-gray-200 pt-4">
                      <p className="font-semibold text-gray-900">{testimonial.author}</p>
                      <p className="text-gray-600">{testimonial.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final CTA Section */}
        <div className="py-32 bg-gradient-to-br from-blue-800 via-blue-900 to-indigo-900 relative overflow-hidden">
          {/* Animated background pattern */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl animate-pulse"></div>
              <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-300 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>
            {/* Animated grid pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center mb-8 animate-on-scroll" id="cta-icon">
              <div className={`p-6 bg-white/10 backdrop-blur-sm rounded-2xl transform transition-all duration-700 ${
                isVisible['cta-icon'] ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
              }`}>
                <ShieldCheck className="h-16 w-16 text-white" />
              </div>
            </div>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 animate-on-scroll transition-all duration-700 transform ${
              isVisible['cta-icon'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`} id="cta-heading">
              Ready to Transform Your Compliance Process?
            </h2>
            <p className={`text-xl text-blue-100 mb-12 max-w-2xl mx-auto animate-on-scroll transition-all duration-700 delay-200 transform ${
              isVisible['cta-heading'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              Join leading companies who've already simplified their audit and compliance workflows
            </p>
            <div className={`flex flex-col sm:flex-row justify-center gap-4 animate-on-scroll transition-all duration-700 delay-300 transform ${
              isVisible['cta-heading'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <a
                href={getAppPublicPathUrl('/signup')}
                className="group inline-flex items-center px-10 py-5 bg-white text-blue-800 rounded-xl font-semibold hover:bg-gray-50 transition-all transform hover:scale-105 shadow-2xl border-2 border-white hover:shadow-white/20"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={getRootDomainUrl('/contact')}
                className="group inline-flex items-center px-10 py-5 bg-white/10 backdrop-blur text-white rounded-xl font-semibold hover:bg-white/20 transition-all transform hover:scale-105 shadow-2xl border-2 border-white/30 hover:border-white/50"
              >
                <Eye className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Schedule a Demo
              </a>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
          {/* Subtle animated background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(59,130,246,0.05),transparent_50%)]" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="group animate-on-scroll" id="stat-1">
                <div className={`transition-all duration-700 transform delay-100 ${
                  isVisible['stat-1'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl mb-4 group-hover:from-blue-800 group-hover:to-indigo-800 transition-all transform group-hover:scale-110 group-hover:rotate-3 shadow-lg group-hover:shadow-xl">
                    <Clock className="h-10 w-10 text-blue-800 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-800 to-indigo-600">40%</p>
                  <p className="text-gray-600 mt-2 font-medium">Audit Prep Time Saved</p>
                </div>
              </div>
              <div className="group animate-on-scroll" id="stat-2">
                <div className={`transition-all duration-700 transform delay-200 ${
                  isVisible['stat-2'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl mb-4 group-hover:from-blue-800 group-hover:to-indigo-800 transition-all transform group-hover:scale-110 group-hover:-rotate-3 shadow-lg group-hover:shadow-xl">
                    <Eye className="h-10 w-10 text-blue-800 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-800 to-indigo-600">100%</p>
                  <p className="text-gray-600 mt-2 font-medium">Data Validation Coverage</p>
                </div>
              </div>
              <div className="group animate-on-scroll" id="stat-3">
                <div className={`transition-all duration-700 transform delay-300 ${
                  isVisible['stat-3'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl mb-4 group-hover:from-blue-800 group-hover:to-indigo-800 transition-all transform group-hover:scale-110 group-hover:rotate-3 shadow-lg group-hover:shadow-xl">
                    <Server className="h-10 w-10 text-blue-800 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-800 to-indigo-600">200+</p>
                  <p className="text-gray-600 mt-2 font-medium">Issues Tracked Per Audit</p>
                </div>
              </div>
              <div className="group animate-on-scroll" id="stat-4">
                <div className={`transition-all duration-700 transform delay-400 ${
                  isVisible['stat-4'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl mb-4 group-hover:from-blue-800 group-hover:to-indigo-800 transition-all transform group-hover:scale-110 group-hover:-rotate-3 shadow-lg group-hover:shadow-xl">
                    <Shield className="h-10 w-10 text-blue-800 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-800 to-indigo-600">24/7</p>
                  <p className="text-gray-600 mt-2 font-medium">Continuous Monitoring</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
      {/* Add custom CSS for animations */}
      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes subtle-float {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          50% {
            transform: translate(10px, -15px) scale(1.02);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes subtle-pulse {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.3;
            transform: scale(1.05);
          }
        }
        @keyframes subtle-wave {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animate-subtle-float {
          animation: subtle-float 12s ease-in-out infinite;
        }
        .animate-subtle-pulse {
          animation: subtle-pulse 8s ease-in-out infinite;
        }
        .animate-subtle-wave {
          animation: subtle-wave 10s ease-in-out infinite;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.4s ease-out forwards;
          opacity: 0;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-300 {
          animation-delay: 0.3s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
        }
        .animation-delay-700 {
          animation-delay: 0.7s;
        }
        .animation-delay-800 {
          animation-delay: 0.8s;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .animation-delay-1200 {
          animation-delay: 1.2s;
        }
        .animation-delay-1400 {
          animation-delay: 1.4s;
        }
        .animation-delay-1600 {
          animation-delay: 1.6s;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}

export default Homepage;